import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { ChevronRightIcon, PlusIcon } from 'lucide-react'
import {
	Chat, ChatAttachment, ChatChannelTabs, ChatDateDivider,
	ChatImage, ChatInput, ChatMessage, ChatMessages,
	DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
	type ChatChannel,
} from '@rentflowdev/ui'
import type { MessageChannel } from '@/types'
import { DEFAULT_LOCALE } from '@/types'
import { useListConnectionsQuery } from '@/api'
import { platformLogo } from '@/lib/platforms'

import type { ConversationMessage } from './model'
import { MANUAL_LOG_CHANNELS, useConversation } from './use-conversation'

const IMAGE_EXT_RE = /\.(jpe?g|png|gif|webp|bmp|svg)$/i

/**
 * Which chat channel an integration opens.
 *
 * `SITE` is a lead source, not a conversation, so it has no entry. WhatsApp is
 * absent too: nothing in the system delivers to it, and offering it would put
 * a menu entry there that can never work. It still appears as a tab when old
 * messages carry it.
 */
const CHANNEL_BY_PLATFORM: Partial<Record<string, MessageChannel>> = {
	AVITO: 'AVITO',
	CIAN: 'CIAN',
	TELEGRAM: 'TELEGRAM',
}

function formatTime(iso: string) {
	return new Date(iso).toLocaleTimeString(DEFAULT_LOCALE, { hour: '2-digit', minute: '2-digit' })
}

/**
 * Day bucket in the VIEWER'S timezone. Slicing the ISO string grouped by UTC
 * day while times rendered locally, so anything sent between midnight and
 * 03:00 MSK filed under the previous day's divider.
 */
function localDayKey(iso: string) {
	const d = new Date(iso)
	const mm = String(d.getMonth() + 1).padStart(2, '0')
	const dd = String(d.getDate()).padStart(2, '0')
	return `${d.getFullYear()}-${mm}-${dd}`
}

function formatDateLabel(dayKey: string) {
	const [y, m, d] = dayKey.split('-').map(Number)
	// Constructed as a LOCAL date: `new Date('YYYY-MM-DD')` parses as UTC
	// midnight and shifts the label a day back west of Greenwich.
	return new Date(y, m - 1, d).toLocaleDateString(DEFAULT_LOCALE, { day: 'numeric', month: 'long' })
}

function formatFileSize(bytes?: number): string | undefined {
	if (!bytes) return undefined
	if (bytes < 1024) return `${bytes} B`
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
	return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

function isImageAttachment(m: ConversationMessage): boolean {
	return !!m.attachmentUrl && IMAGE_EXT_RE.test(m.attachmentName ?? m.attachmentUrl)
}

/*
 * The in-thread meeting card from the design is not rendered here.
 *
 * It used to hang off `direction === 'AUTO'`, a value no layer produces —
 * Prisma stores INBOUND|OUTBOUND and both protos add only UNSPECIFIED — so the
 * branch was unreachable from the day it was written. Bringing the card back
 * needs a producer first: something has to write the meeting into the thread.
 * ChatEventCard is still in the UI package, waiting for it.
 */

export interface ConversationChatProps {
	/** The deal this conversation belongs to. */
	dealId?: string
	/**
	 * Set on a rental. Its thread continues the deal's rather than starting
	 * empty — signing a contract does not begin a new conversation with the
	 * same person, and the history is what the landlord refers back to.
	 */
	rentalId?: string
	onCollapse?: () => void
}

/**
 * The chat, used by every page that has one — deal, tenant, rental.
 *
 * There used to be two of these. The deal's rendered attachments, images,
 * viewing cards and read ticks; the rental's rendered plain paragraphs. Same
 * conversation, two different products depending on the page you came from.
 * Everything specific to a store now lives behind `useConversation`, so a fix
 * to how a message looks lands everywhere at once.
 */
export function ConversationChat({ dealId, rentalId, onCollapse }: ConversationChatProps) {
	// Channels opened by hand this session. A tab has to survive being added
	// before anything is written to it, and until then there is nothing in the
	// data to remember it by.
	const [addedChannels, setAddedChannels] = useState<MessageChannel[]>([])
	const [activeChannel, setActiveChannel] = useState<MessageChannel | null>(null)

	// TanStack Router does not remount on a param-only navigation, so without
	// this the tab selected on deal A filtered deal B's thread into an empty
	// screen, and A's hand-opened tabs followed the user around. Reset during
	// render (the React-endorsed key-comparison pattern) rather than in an
	// effect, which would flash one frame of the previous conversation.
	const conversationKey = dealId ?? rentalId
	const [prevKey, setPrevKey] = useState(conversationKey)
	if (prevKey !== conversationKey) {
		setPrevKey(conversationKey)
		setAddedChannels([])
		setActiveChannel(null)
	}

	const { t } = useTranslation()
	const {
		messages: allMessages, isSending, sendFailed, sendError,
		canWrite, send, isManualLog, hasOlder, loadOlder,
	} = useConversation({ dealId, rentalId })
	const { data: connectionsData } = useListConnectionsQuery()

	/** What this account could talk on, if it chose to. */
	const availableChannels = useMemo(() => {
		// A deal writes through integrations. The manual log (rental without a
		// deal) writes to its own table and needs none — it records exchanges
		// held in a messenger the product is not connected to, so offering a
		// channel here that canWrite() would then reject is never allowed.
		if (!dealId) return rentalId ? [...MANUAL_LOG_CHANNELS] : []
		const out = new Set<MessageChannel>()
		for (const c of connectionsData?.connections ?? []) {
			if (!c.isActive) continue
			const channel = CHANNEL_BY_PLATFORM[c.platform]
			if (!channel) continue
			out.add(channel)
		}
		return [...out]
	}, [connectionsData, dealId, rentalId])

	// Only what this conversation actually uses, plus what was opened by hand.
	// The panel used to list every channel it knew about, so a thread that had
	// only ever seen Avito still showed four tabs, three of them empty.
	const visibleChannels = useMemo(() => {
		const seen = new Set<MessageChannel>()
		for (const m of allMessages) seen.add(m.channel)
		for (const c of addedChannels) seen.add(c)
		return [...seen]
	}, [allMessages, addedChannels])

	const addableChannels = useMemo(
		() => availableChannels.filter((c) => !visibleChannels.includes(c)),
		[availableChannels, visibleChannels],
	)

	// An empty thread still offers the first writable channel: a brand-new
	// deal has to accept its first message without a detour through the «+»
	// menu — the old panel allowed writing immediately, and "sending is
	// unavailable" on a writable conversation is simply false.
	const fallbackChannel = useMemo(
		() =>
			visibleChannels.length === 0
				? (availableChannels.find((c) => canWrite(c)) ?? null)
				: null,
		[visibleChannels, availableChannels, canWrite],
	)
	const effectiveChannels = fallbackChannel ? [fallbackChannel] : visibleChannels

	// Nothing is selected until there is something to select; picking a tab
	// during render would fight the user's own choice on the next pass. A
	// selection that no longer matches any tab (a refetch shrank the content)
	// falls back instead of filtering the thread into a void.
	const currentChannel =
		activeChannel && effectiveChannels.includes(activeChannel)
			? activeChannel
			: (effectiveChannels[0] ?? null)

	const messages = useMemo(
		() => allMessages.filter((m) => m.channel === currentChannel),
		[allMessages, currentChannel],
	)

	const channelTabs: ChatChannel[] = useMemo(
		() => effectiveChannels.map((c) => ({
			id: c,
			label: t(`messageChannels.${c}`),
			logo: platformLogo(c),
		})),
		[effectiveChannels, t],
	)

	const grouped = useMemo(() => {
		const map = new Map<string, ConversationMessage[]>()
		for (const m of messages) {
			const key = localDayKey(m.sentAt)
			const bucket = map.get(key) ?? []
			bucket.push(m)
			map.set(key, bucket)
		}
		return [...map.entries()].sort(([a], [b]) => a.localeCompare(b))
	}, [messages])

	const composerEnabled = currentChannel !== null && canWrite(currentChannel)

	async function handleSend(text: string): Promise<boolean> {
		if (!currentChannel || isSending) return false
		return send(currentChannel, text)
	}

	function renderMessage(m: ConversationMessage) {
		const align = m.direction === 'INBOUND' ? 'start' : 'end'
		const variant = m.direction === 'INBOUND' ? 'incoming' : 'outgoing'
		const time = formatTime(m.sentAt)
		// Undefined read state is not "unread": a rental's merged view does not
		// carry the column, and a grey tick there would be an invented claim.
		const readStatus =
			m.direction !== 'INBOUND' && m.isRead !== undefined
				? m.isRead
					? 'read'
					: 'sent'
				: null

		if (m.attachmentUrl && isImageAttachment(m)) {
			return (
				<ChatImage
					key={m.id}
					src={m.attachmentUrl}
					alt={m.attachmentName ?? ''}
					time={time}
					readStatus={readStatus}
					align={align}
				/>
			)
		}

		if (m.attachmentUrl) {
			return (
				<ChatAttachment
					key={m.id}
					fileName={m.attachmentName ?? 'attachment'}
					fileSize={formatFileSize(m.attachmentSize)}
					downloadLabel={t('common.download')}
					time={time}
					align={align}
					onDownload={() => {
						if (m.attachmentUrl) window.open(m.attachmentUrl, '_blank')
					}}
				/>
			)
		}

		return (
			<ChatMessage key={m.id} variant={variant} time={time} readStatus={readStatus}>
				{m.content}
			</ChatMessage>
		)
	}

	return (
		<Chat className="h-full p-4">
			<div className="flex items-center gap-2 border-b border-(--color-border) pb-2">
				<ChatChannelTabs
					channels={channelTabs}
					activeId={currentChannel ?? ''}
					onChange={(id) => setActiveChannel(id as MessageChannel)}
					className="flex-1"
					/* The button was wired to `() => {}` — it looked like an action
					   and did nothing. It now offers the channels this account has
					   an integration for and is not already showing, so a channel
					   with nothing behind it is never offered. It stays in place
					   when that list is empty and points at settings instead:
					   hiding it made it look lost, and "everything is already
					   open" is an answer worth giving. */
					addSlot={
						<DropdownMenu>
							<DropdownMenuTrigger
								render={
									<button
										type="button"
										aria-label={t('chat.addChannel')}
										className="flex size-8 shrink-0 items-center justify-center rounded-(--radius-sm) border border-(--color-border) text-(--color-muted-foreground) transition-colors hover:text-(--color-foreground)"
									/>
								}
							>
								<PlusIcon className="size-4" />
							</DropdownMenuTrigger>
							<DropdownMenuContent align="start" className="min-w-40">
								{addableChannels.map((c) => {
									const logo = platformLogo(c)
									return (
										<DropdownMenuItem
											key={c}
											onClick={() => {
												setAddedChannels((prev) => [...prev, c])
												setActiveChannel(c)
											}}
										>
											{logo && (
												<img src={logo} alt="" className="size-4 shrink-0 rounded-[3px] object-contain" />
											)}
											{t(`messageChannels.${c}`)}
										</DropdownMenuItem>
									)
								})}
								{addableChannels.length > 0 && <DropdownMenuSeparator />}
								{/* Always present, not only when the list above is empty —
								    hiding it behind "no addable channels" forced a second
								    trip through the + button to reach settings. */}
								<DropdownMenuItem render={<Link to="/settings/integrations" />}>
									{t('chat.connectIntegration')}
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>
					}
				/>
				{onCollapse && (
					<button
						type="button"
						onClick={onCollapse}
						className="shrink-0 rounded-(--radius-sm) p-1 text-(--color-muted-foreground) transition-colors hover:text-(--color-foreground) hover:bg-(--color-muted)"
					>
						<ChevronRightIcon className="size-4" />
					</button>
				)}
			</div>

			<ChatMessages>
				{/* Older history exists beyond the loaded page. Without this the
				    page cap silently truncated the thread — and a channel whose
				    rows all fell past it lost its tab entirely. */}
				{hasOlder && (
					<button
						type="button"
						onClick={loadOlder}
						className="self-center rounded-(--radius-sm) px-3 py-1 text-xs text-(--color-muted-foreground) transition-colors hover:text-(--color-foreground) hover:bg-(--color-muted)"
					>
						{t('chat.loadOlder')}
					</button>
				)}
				{grouped.map(([day, items]) => (
					<div key={day} className="flex flex-col gap-2">
						<ChatDateDivider>{formatDateLabel(day)}</ChatDateDivider>
						{items.map(renderMessage)}
					</div>
				))}
			</ChatMessages>

			{sendFailed && (
				<p className="text-sm text-(--color-destructive)">
					{sendError ?? t('chat.sendError')}
				</p>
			)}

			{/* Always present, disabled where there is nothing to write to. The
			    panel is the same shape on every page and in every state — an
			    empty thread is an empty thread, not a different screen.
			    The placeholder carries the reason: a greyed box still inviting
			    you to "write a message" is an invitation it will not honour. */}
			<ChatInput
				placeholder={
					composerEnabled
						? isManualLog
							? // Recorded, not delivered — pretending otherwise is how
								// the SENT-but-never-sent rows of #305 came to exist.
								t('chat.manualLogPlaceholder')
							: t('common.messagePlaceholder')
						: t('chat.sendUnavailable')
				}
				onSend={handleSend}
				onAttach={() => {}}
				disabled={!composerEnabled}
			/>
		</Chat>
	)
}
